import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  Flex,
  IconButton,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { useBrandedTranslation } from "../../../../../common/hooks/useBrandedTranslation"
import { FhResponseData } from "../../../../../common/types/search"
import { getDrilledCategories, getIsFirstLevelCategory } from "../../../../../common/utils/search-utils"
import { CCategories, CClose } from "../../../../shared/Icons"
import ScrollShadow from "../../../../shared/ScrollShadow"
import { LowerLevelCategoryTile } from "../CategoriesList/LowerLevelCategoryTile/LowerLevelCategoryTile"
import CategoryBreadcrumbs from "../CategoryBreadcrumbs/CategoryBreadcrumbs"

type Props = {
  categories: FhResponseData
  breadcrumbsData: FhResponseData
  handleCategorySelect: (_urlParams: string, categoryValue: string, isActive: boolean) => void
  searchTerm: string
}

const MobileCategoriesFilter = ({ categories, breadcrumbsData, handleCategorySelect, searchTerm }: Props) => {
  const { t } = useBrandedTranslation()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const handleSelect = (url_params: string, categoryValue: string, isActive: boolean) => {
    /* Close the layer, wait for the animation and apply the selection */
    onClose()
    setTimeout(() => handleCategorySelect(url_params, categoryValue, isActive), 200)
  }

  const getAppliedCategoryName = () => {
    const drilledCategories = getDrilledCategories(breadcrumbsData)

    if (drilledCategories.length === 1) {
      return drilledCategories[0].name.value
    }

    return drilledCategories[1].name.value
  }

  /* Determine the header text based on category level */
  const isFirstLevelCategory = getIsFirstLevelCategory(breadcrumbsData)
  const headerLabel = isFirstLevelCategory ? t("search-categories-for") : t("search-categories-in")
  const headerParam = isFirstLevelCategory ? searchTerm : getAppliedCategoryName()

  return (
    <Box display={{ base: "block", md: "none" }}>
      <Button
        rightIcon={<CCategories />}
        onClick={onOpen}
        h="11"
        pl="2"
        pr="2.5"
        w="100%"
        _active={{ bgColor: "gray.900", color: "white" }}
        aria-label="Open mobile categories filter"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text as="span" fontSize="xs" fontWeight="medium" lineHeight="4">
          {`${t("search-select-categories")} (${categories.length})`}
        </Text>
      </Button>

      <Drawer placement="right" onClose={onClose} isOpen={isOpen} size="full">
        <DrawerContent height="100dvh">
          <Flex
            as="header"
            pl={{ base: "0.5", sm: "2.5" }}
            pr={{ base: "4", sm: "6" }}
            minH="15"
            gap="2"
            fontSize="sm"
            bgColor="gray.200"
            alignItems="center"
            justifyContent="space-between"
          >
            <IconButton
              icon={<CClose />}
              bg="transparent"
              aria-label="Close mobile categories filter"
              boxSize="12"
              onClick={onClose}
            />
            <Text textAlign="end" noOfLines={2}>
              <Text as="span" mr="1">
                {headerLabel}
              </Text>
              <Text as="span" fontWeight="bold">
                {headerParam}
              </Text>
            </Text>
          </Flex>
          <DrawerBody px={{ base: "4", sm: "6" }} pt="4" pb="12" fontSize="sm">
            <CategoryBreadcrumbs
              categories={categories}
              breadcrumbsData={breadcrumbsData}
              isOverlayView
              closeOverlay={onClose}
            />
            <SimpleGrid columns={{ base: 1, xs: 2 }} gap="6" rowGap={{ base: "2", xs: "4" }}>
              {categories.map((category: FhResponseData) => (
                <LowerLevelCategoryTile
                  key={category.value.value}
                  category={category}
                  handleCategorySelect={handleSelect}
                  isOverlayView
                />
              ))}
            </SimpleGrid>

            <ScrollShadow isVisible={true} height="20" />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}

export default MobileCategoriesFilter
